import { useEffect, useRef, useState } from 'react';
import { VISUAL_PHASE_DEBOUNCE_MS } from '@/shared/config/terminal.config';
import type { ScanPhase } from './useScanLifecycle';

/**
 * Visual adapter over `useScanLifecycle.phase`.
 *
 * Mirrors the real phase except for `comparing → scanning` reversions, which
 * are held at `comparing` for {@link VISUAL_PHASE_DEBOUNCE_MS}. Fast-path:
 * `matched` and `error` propagate immediately and cancel any pending debounce.
 *
 * Pure presentation — never read by navigation effects.
 */
export function useVisualScanPhase(phase: ScanPhase): ScanPhase {
  const [visualPhase, setVisualPhase] = useState<ScanPhase>(phase);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const visualPhaseRef = useRef<ScanPhase>(phase);

  const clearTimer = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const commit = (next: ScanPhase) => {
    visualPhaseRef.current = next;
    setVisualPhase(next);
  };

  useEffect(() => {
    // Fast-path: matched/error always win, cancel any pending revert.
    if (phase === 'matched' || phase === 'error') {
      clearTimer();
      commit(phase);
      return;
    }

    // Forward into comparing: immediate, cancel any pending revert.
    if (phase === 'comparing') {
      clearTimer();
      commit('comparing');
      return;
    }

    // Revert comparing → scanning: debounce.
    if (phase === 'scanning' && visualPhaseRef.current === 'comparing') {
      clearTimer();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        commit('scanning');
      }, VISUAL_PHASE_DEBOUNCE_MS);
      return;
    }

    // All other transitions: immediate.
    clearTimer();
    commit(phase);
  }, [phase]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => clearTimer();
  }, []);

  return visualPhase;
}
